/**
 * Timezone utilities for the Qualid dashboard.
 *
 * PHP controllers run SET time_zone = '+00:00' so MySQL TIMESTAMP fields are
 * returned as UTC. parseServerDate() appends '+00:00' before parsing.
 *
 * Display timezone is derived from the user's phone country_code stored in
 * localStorage (e.g. "20" for Egypt → "Africa/Cairo"). Falls back to the
 * browser's own timezone if the code is not in the map.
 */

const SERVER_UTC_OFFSET = '+00:00'

// Phone country-code (numeric string, no '+') → IANA timezone
const PHONE_CODE_TO_TZ = {
  // Middle East & North Africa
  20: 'Africa/Cairo', // Egypt UTC+2
  212: 'Africa/Casablanca', // Morocco UTC+1
  213: 'Africa/Algiers', // Algeria UTC+1
  216: 'Africa/Tunis', // Tunisia UTC+1
  218: 'Africa/Tripoli', // Libya UTC+2
  249: 'Africa/Khartoum', // Sudan UTC+3
  961: 'Asia/Beirut', // Lebanon UTC+2/+3
  962: 'Asia/Amman', // Jordan UTC+2/+3
  963: 'Asia/Damascus', // Syria UTC+2/+3
  964: 'Asia/Baghdad', // Iraq UTC+3
  965: 'Asia/Kuwait', // Kuwait UTC+3
  966: 'Asia/Riyadh', // Saudi Arabia UTC+3
  967: 'Asia/Aden', // Yemen UTC+3
  968: 'Asia/Muscat', // Oman UTC+4
  970: 'Asia/Hebron', // Palestine UTC+2/+3
  971: 'Asia/Dubai', // UAE UTC+4
  972: 'Asia/Jerusalem', // Israel UTC+2/+3
  973: 'Asia/Bahrain', // Bahrain UTC+3
  974: 'Asia/Qatar', // Qatar UTC+3
  975: 'Asia/Thimphu', // Bhutan UTC+6
  976: 'Asia/Ulaanbaatar', // Mongolia UTC+8
  977: 'Asia/Kathmandu', // Nepal UTC+5:45

  // Africa (sub-Saharan)
  27: 'Africa/Johannesburg', // South Africa UTC+2
  233: 'Africa/Accra', // Ghana UTC+0
  234: 'Africa/Lagos', // Nigeria UTC+1
  251: 'Africa/Addis_Ababa', // Ethiopia UTC+3
  254: 'Africa/Nairobi', // Kenya UTC+3
  255: 'Africa/Dar_es_Salaam', // Tanzania UTC+3
  256: 'Africa/Kampala', // Uganda UTC+3

  // Europe
  7: 'Europe/Moscow', // Russia UTC+3
  30: 'Europe/Athens', // Greece UTC+2/+3
  31: 'Europe/Amsterdam', // Netherlands UTC+1/+2
  32: 'Europe/Brussels', // Belgium UTC+1/+2
  33: 'Europe/Paris', // France UTC+1/+2
  34: 'Europe/Madrid', // Spain UTC+1/+2
  39: 'Europe/Rome', // Italy UTC+1/+2
  40: 'Europe/Bucharest', // Romania UTC+2/+3
  41: 'Europe/Zurich', // Switzerland UTC+1/+2
  43: 'Europe/Vienna', // Austria UTC+1/+2
  44: 'Europe/London', // UK UTC+0/+1
  45: 'Europe/Copenhagen', // Denmark UTC+1/+2
  46: 'Europe/Stockholm', // Sweden UTC+1/+2
  47: 'Europe/Oslo', // Norway UTC+1/+2
  48: 'Europe/Warsaw', // Poland UTC+1/+2
  49: 'Europe/Berlin', // Germany UTC+1/+2
  90: 'Europe/Istanbul', // Turkey UTC+3
  380: 'Europe/Kiev', // Ukraine UTC+2/+3

  // Americas
  1: 'America/New_York', // USA/Canada (eastern default)
  51: 'America/Lima', // Peru UTC-5
  52: 'America/Mexico_City', // Mexico UTC-6/-5
  54: 'America/Argentina/Buenos_Aires', // Argentina UTC-3
  55: 'America/Sao_Paulo', // Brazil UTC-3/-2
  56: 'America/Santiago', // Chile UTC-4/-3
  57: 'America/Bogota', // Colombia UTC-5
  58: 'America/Caracas', // Venezuela UTC-4

  // Asia & Pacific
  60: 'Asia/Kuala_Lumpur', // Malaysia UTC+8
  61: 'Australia/Sydney', // Australia UTC+10/+11
  62: 'Asia/Jakarta', // Indonesia UTC+7
  63: 'Asia/Manila', // Philippines UTC+8
  64: 'Pacific/Auckland', // New Zealand UTC+12/+13
  65: 'Asia/Singapore', // Singapore UTC+8
  66: 'Asia/Bangkok', // Thailand UTC+7
  81: 'Asia/Tokyo', // Japan UTC+9
  82: 'Asia/Seoul', // South Korea UTC+9
  84: 'Asia/Ho_Chi_Minh', // Vietnam UTC+7
  86: 'Asia/Shanghai', // China UTC+8
  91: 'Asia/Kolkata', // India UTC+5:30
  92: 'Asia/Karachi', // Pakistan UTC+5
  94: 'Asia/Colombo', // Sri Lanka UTC+5:30
  95: 'Asia/Rangoon', // Myanmar UTC+6:30
  98: 'Asia/Tehran' // Iran UTC+3:30/+4:30
}

/**
 * Return the IANA timezone for the currently logged-in user.
 * Falls back to the browser's own timezone.
 */
export function getUserTimezone () {
  try {
    const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
    const code = String(userData.country_code || '').replace('+', '').trim()
    return PHONE_CODE_TO_TZ[code] || Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (e) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }
}

/**
 * Parse a MySQL datetime string (no timezone) as UTC.
 * Converts "2026-03-03 14:30:00" → new Date("2026-03-03T14:30:00+00:00")
 */
function parseServerDate (str) {
  if (!str) return null
  return new Date(str.replace(' ', 'T') + SERVER_UTC_OFFSET)
}

/**
 * Format a server timestamp as a date string in the user's timezone.
 * e.g. "03 Mar 2026"
 */
export function formatDate (str, timezone) {
  const d = parseServerDate(str)
  if (!d || isNaN(d)) return '—'
  const tz = timezone || getUserTimezone()
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(d)
}

/**
 * Format a server timestamp as date + time in the user's timezone.
 * e.g. "03 Mar 2026, 09:30 AM"
 */
export function formatDateTime (str, timezone) {
  const d = parseServerDate(str)
  if (!d || isNaN(d)) return '—'
  const tz = timezone || getUserTimezone()
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(d)
}

/**
 * Format a server timestamp as time-only in the user's timezone.
 * e.g. "09:30 AM"
 */
export function formatTime (str, timezone) {
  const d = parseServerDate(str)
  if (!d || isNaN(d)) return '—'
  const tz = timezone || getUserTimezone()
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(d)
}

/**
 * Format a timeline hour bucket for bar-chart labels.
 * Single-day: "9AM", "2PM". Multi-day: "3/3 9AM".
 */
export function formatHourLabel (str, timezone, multiDay) {
  const d = parseServerDate(str)
  if (!d || isNaN(d)) return ''
  const tz = timezone || getUserTimezone()

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    hour12: true,
    day: 'numeric',
    month: 'numeric'
  }).formatToParts(d)

  const get = type => parts.find(p => p.type === type)?.value || ''
  const hour = get('hour')
  const dayperiod = get('dayPeriod').replace(/\./g, '')

  if (multiDay) {
    return `${get('month')}/${get('day')} ${hour}${dayperiod}`
  }
  return `${hour}${dayperiod}`
}
